import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, AlertTriangle, CheckCircle2, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import { formatIDR } from "@/lib/constants";

/**
 * BalanceSheetReport — 2026-10-18 (user request P2).
 *
 * Neraca per `as_of`. Pos otomatis dari engine A.R. + opening balance manual
 * (per tahun, diisi BOD/FAT). Validasi Aset = Liabilitas + Ekuitas.
 */
export default function BalanceSheetReport({ asOf, canExport }) {
  const { user } = useAuth();
  const { canPerm } = useAccess();
  const canEditOB = canPerm(user, "finance_report", "update");
  const [bs, setBs] = useState(null);
  const [ob, setOb] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({ kas_bank: 0, aset_tetap: 0, investasi: 0, utang_jangka_panjang: 0, modal_disetor: 0, laba_ditahan: 0 });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const year = useMemo(() => parseInt(asOf.slice(0, 4), 10), [asOf]);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const [b, o] = await Promise.all([
        api.get("/finance/reports/balance-sheet", { params: { as_of: asOf } }),
        api.get("/finance/reports/opening-balance", { params: { year } }),
      ]);
      setBs(b.data);
      setOb(o.data);
      setForm({
        kas_bank: o.data.kas_bank || 0, aset_tetap: o.data.aset_tetap || 0,
        investasi: o.data.investasi || 0, utang_jangka_panjang: o.data.utang_jangka_panjang || 0,
        modal_disetor: o.data.modal_disetor || 0, laba_ditahan: o.data.laba_ditahan || 0,
      });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Gagal memuat Neraca.");
    }
  }, [asOf, year]);

  useEffect(() => { load(); }, [load]);

  const saveOB = async () => {
    setSaving(true);
    try {
      await api.put("/finance/reports/opening-balance", { year, ...form });
      toast.success(`Saldo awal ${year} tersimpan.`);
      setEdit(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan saldo awal.");
    } finally {
      setSaving(false);
    }
  };

  const dlXlsx = async () => {
    try {
      const res = await api.get("/finance/reports/balance-sheet.xlsx", { params: { as_of: asOf }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `neraca_${asOf}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Gagal mengunduh XLSX."); }
  };

  if (err) return <div className="p-6 bg-rose-50 border border-rose-200 text-rose-800 text-xs">{err}</div>;
  if (!bs) return <div className="p-8 text-center text-slate-400 text-sm" data-testid="bs-loading">Memuat Neraca…</div>;

  const Line = ({ label, amount, source, bold, testid }) => (
    <div className={`flex justify-between items-baseline px-3 py-1.5 border-b border-slate-100 last:border-0 ${bold ? "bg-slate-50 font-bold" : ""}`} data-testid={testid}>
      <span className={`text-xs ${bold ? "text-[#0B1D3A]" : "text-slate-700"}`}>{label}</span>
      <div className="flex items-center gap-2">
        {source === "needs_input" && <span className="text-[9px] uppercase bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 font-semibold">Butuh Input</span>}
        <span className={`tabular-nums text-xs ${bold ? "text-[#0B1D3A]" : "text-slate-800"}`}>{formatIDR(amount)}</span>
      </div>
    </div>
  );

  const InputRow = ({ k, label }) => (
    <div className="flex items-center gap-3 px-3 py-1.5 border-b border-slate-100">
      <label className="text-xs text-slate-600 flex-1">{label}</label>
      <input type="number" value={form[k]} onChange={(e) => setForm({ ...form, [k]: Number(e.target.value) || 0 })}
        className="border border-slate-300 py-1 px-2 text-xs w-44 text-right tabular-nums" data-testid={`bs-ob-${k}`} />
    </div>
  );

  return (
    <div className="space-y-3" data-testid="balance-sheet-report">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[11px] text-slate-500">Per <b>{asOf}</b> · Tahun berjalan <b>{year}</b></div>
        <div className="flex items-center gap-2">
          {canEditOB && !edit && (
            <button type="button" onClick={() => setEdit(true)} data-testid="bs-edit-ob"
              className="h-8 px-3 border border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100 text-[11px] uppercase tracking-wider font-semibold">
              Atur Saldo Awal {year}
            </button>
          )}
          <button type="button" disabled={!canExport} onClick={dlXlsx} data-testid="bs-export-xlsx"
            className="h-8 px-3 border border-[#0B1D3A] bg-[#0B1D3A] text-white hover:bg-[#122a4d] text-[11px] uppercase tracking-wider font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
            <Download size={12} /> XLSX
          </button>
        </div>
      </div>

      {!bs.is_seeded && (
        <div className="p-3 bg-amber-50 border border-amber-200 text-[11px] text-amber-900 flex items-start gap-2" data-testid="bs-needs-ob">
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
          <div>
            <b>Saldo awal {year} belum diisi.</b> Pos Kas & Bank, Aset Tetap, Modal Disetor, Laba Ditahan ditandai <i>Butuh Input</i> — tidak dipalsukan menjadi nol. Isi via tombol di atas (butuh perm Laporan Keuangan → Ubah).
          </div>
        </div>
      )}

      {edit && (
        <div className="border border-amber-300 bg-amber-50 p-3 space-y-1" data-testid="bs-ob-editor">
          <div className="text-xs font-bold text-amber-900 mb-2">Saldo Awal Tahun {year}</div>
          <InputRow k="kas_bank" label="Kas & Bank" />
          <InputRow k="aset_tetap" label="Aset Tetap (bruto)" />
          <InputRow k="investasi" label="Investasi Jangka Panjang" />
          <InputRow k="utang_jangka_panjang" label="Utang Jangka Panjang" />
          <InputRow k="modal_disetor" label="Modal Disetor" />
          <InputRow k="laba_ditahan" label="Laba Ditahan (akumulasi)" />
          <div className="flex gap-2 pt-2">
            <button type="button" disabled={saving} onClick={saveOB} data-testid="bs-ob-save"
              className="h-8 px-4 bg-emerald-600 text-white hover:bg-emerald-700 text-[11px] uppercase tracking-wider font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
              <Save size={12} /> {saving ? "Menyimpan…" : "Simpan"}
            </button>
            <button type="button" onClick={() => setEdit(false)} className="h-8 px-4 border border-slate-300 text-slate-700 text-[11px] uppercase tracking-wider font-semibold">Batal</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="border border-slate-200 bg-white" data-testid="bs-aset">
          <div className="px-3 py-2 bg-[#0B1D3A] text-white text-xs font-bold uppercase tracking-wider">Aset</div>
          <div className="px-3 py-1.5 bg-slate-100 text-[10px] uppercase font-semibold text-slate-600">Aset Lancar</div>
          <Line label="Kas & Bank" amount={bs.aset.lancar.kas_bank.amount} source={bs.aset.lancar.kas_bank.source} testid="bs-kas-bank" />
          <Line label="Piutang Usaha" amount={bs.aset.lancar.piutang_usaha.amount} source={bs.aset.lancar.piutang_usaha.source} testid="bs-piutang-usaha" />
          <Line label="Piutang Retensi" amount={bs.aset.lancar.piutang_retensi.amount} source={bs.aset.lancar.piutang_retensi.source} testid="bs-piutang-retensi" />
          <Line label="Klaim Retensi Belum Dibayar" amount={bs.aset.lancar.klaim_retensi.amount} source={bs.aset.lancar.klaim_retensi.source} testid="bs-klaim-retensi" />
          <Line label="Sub Total Aset Lancar" amount={bs.aset.lancar.total} bold testid="bs-sub-lancar" />
          <div className="px-3 py-1.5 bg-slate-100 text-[10px] uppercase font-semibold text-slate-600">Aset Tidak Lancar</div>
          <Line label="Aset Tetap" amount={bs.aset.tidak_lancar.aset_tetap.amount} source={bs.aset.tidak_lancar.aset_tetap.source} />
          <Line label="Investasi" amount={bs.aset.tidak_lancar.investasi.amount} source={bs.aset.tidak_lancar.investasi.source} />
          <Line label="Sub Total" amount={bs.aset.tidak_lancar.total} bold />
          <Line label="TOTAL ASET" amount={bs.aset.total} bold testid="bs-total-aset" />
        </div>
        <div className="border border-slate-200 bg-white" data-testid="bs-pasiva">
          <div className="px-3 py-2 bg-[#0B1D3A] text-white text-xs font-bold uppercase tracking-wider">Liabilitas + Ekuitas</div>
          <div className="px-3 py-1.5 bg-slate-100 text-[10px] uppercase font-semibold text-slate-600">Liabilitas Lancar</div>
          <Line label={`Utang PPh (${bs.liabilitas.lancar.utang_pph.count} invoice)`} amount={bs.liabilitas.lancar.utang_pph.amount} source="auto" testid="bs-utang-pph" />
          <Line label="Sub Total" amount={bs.liabilitas.lancar.total} bold />
          <div className="px-3 py-1.5 bg-slate-100 text-[10px] uppercase font-semibold text-slate-600">Liabilitas Tidak Lancar</div>
          <Line label="Utang Jangka Panjang" amount={bs.liabilitas.tidak_lancar.utang_jangka_panjang.amount} source={bs.liabilitas.tidak_lancar.utang_jangka_panjang.source} />
          <Line label="TOTAL LIABILITAS" amount={bs.liabilitas.total} bold testid="bs-total-liab" />
          <div className="px-3 py-1.5 bg-slate-100 text-[10px] uppercase font-semibold text-slate-600">Ekuitas</div>
          <Line label="Modal Disetor" amount={bs.ekuitas.modal_disetor.amount} source={bs.ekuitas.modal_disetor.source} />
          <Line label="Laba Ditahan" amount={bs.ekuitas.laba_ditahan.amount} source={bs.ekuitas.laba_ditahan.source} />
          <Line label="Laba Berjalan YTD" amount={bs.ekuitas.laba_berjalan_ytd.amount} source="auto" testid="bs-laba-ytd" />
          <Line label="TOTAL EKUITAS" amount={bs.ekuitas.total} bold />
          <Line label="TOTAL PASIVA" amount={bs.total_pasiva} bold testid="bs-total-pasiva" />
        </div>
      </div>

      <div className={`p-3 border flex items-center gap-2 text-xs font-semibold ${bs.validasi.balanced ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-amber-50 border-amber-300 text-amber-900"}`}
        data-testid="bs-validation">
        {bs.validasi.balanced ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
        <span>Validasi: {bs.validasi.catatan} Selisih: <b className="tabular-nums">{formatIDR(bs.validasi.selisih)}</b></span>
      </div>
    </div>
  );
}
